import { demoRegistry, getDemo, nextDemoId } from "@/demos/registry";
import type { DemoAction, DemoControlState, DemoId } from "@/demos/types";
import { resolvedCtaUrl } from "@/app/externalNavigation";
import { RuntimeErrorDetails } from "@/app/RuntimeErrorBoundary";
import { LandingPanel, StatusPanel, DemoControls } from "@/app/ExperienceHudSurfaces";
import type { RuntimeErrorRecord } from "@/runtime/runtimeErrors";
import type { RuntimeMode, RuntimePhase } from "@/runtime/types";

export { DemoControls, LandingPanel, StatusPanel } from "@/app/ExperienceHudSurfaces";
export type { LandingPanelProps, StatusPanelProps } from "@/app/ExperienceHudSurfaces";

export interface ExperienceHudProps {
  webglAvailable: boolean;
  started: boolean;
  mode: RuntimeMode;
  phase: RuntimePhase;
  activeDemoId: DemoId;
  activeDemoTitle: string;
  placementProgress: number;
  error: string | null;
  notice: string | null;
  loadingNext: boolean;
  runtimeError: RuntimeErrorRecord | null;
  controls: DemoControlState;
  onStart: () => void;
  onVisit: () => void;
  onRetry: () => void;
  onContinue: () => void;
  onReposition: () => void;
  onUseCamera: () => void;
  onNextDemo: () => void;
  onDismissRuntimeError: () => void;
  onAction: (action: DemoAction) => void;
}

export function ExperienceHud(props: ExperienceHudProps) {
  return (
    <>
      <header className="brand-lockup" aria-label="Rising Pixel">
        <span className="brand-spark" aria-hidden="true"><i /><i /><i /></span>
        <span>RISING</span><span className="brand-accent">PIXEL</span>
      </header>

      {props.runtimeError ? (
        <aside className="runtime-error-overlay" role="alert" data-testid="runtime-error">
          <div className="runtime-error-card">
            <span className="runtime-error-kicker">Rising Pixel / exception</span>
            <h2>Technical exception</h2>
            <p>{props.runtimeError.message}</p>
            <span className="runtime-error-context">{props.runtimeError.context}</span>
            <RuntimeErrorDetails record={props.runtimeError} />
            <div className="runtime-error-actions">
              <button type="button" onClick={props.onDismissRuntimeError}>Dismiss</button>
              <button type="button" onClick={() => window.location.reload()}>Reload</button>
            </div>
          </div>
        </aside>
      ) : null}

      {!props.webglAvailable ? <LandingPanel variant="compatibility" onVisit={props.onVisit} /> : null}
      {props.webglAvailable && (!props.started || props.phase === "idle") ? <LandingPanel variant="landing" onStart={props.onStart} onVisit={props.onVisit} /> : null}

      {props.webglAvailable && props.started ? (
        <section className="experience-hud">
          <div className="chapter-heading"><h1>{props.activeDemoTitle}</h1></div>
          <StatusPanel
            phase={props.phase}
            mode={props.mode}
            progress={props.placementProgress}
            error={props.error}
            notice={props.notice}
            onRetry={props.onRetry}
            onContinue={props.onContinue}
          />
          {props.phase === "placed" ? <DemoControls id={props.activeDemoId} controls={props.controls} dispatch={props.onAction} /> : null}

          <nav className="utility-controls" aria-label="Experience controls" data-ui-control>
            {props.phase === "placed" ? (
              <button className="icon-button" type="button" onClick={props.onReposition} aria-label="Reposition experience" title="Reposition experience">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 3v4M12 17v4M3 12h4M17 12h4M8.5 8.5 5.7 5.7M15.5 8.5l2.8-2.8M8.5 15.5l-2.8 2.8M15.5 15.5l2.8 2.8" />
                  <circle cx="12" cy="12" r="3.25" />
                </svg>
              </button>
            ) : null}
            {props.mode === "webxr" && props.phase !== "recovering" ? <button type="button" onClick={props.onUseCamera}>Use camera</button> : null}
          </nav>

          <div className="experience-dock" data-ui-control>
            <ol className="demo-progress" aria-label="Experience progress">
              {demoRegistry.map((demo) => <li key={demo.id} aria-current={demo.id === props.activeDemoId ? "step" : undefined}><span>{demo.title}</span></li>)}
            </ol>
            <div className="primary-actions">
              {props.activeDemoId === "engage" && props.phase === "placed" ? (
                <a className="cta-button" href={resolvedCtaUrl()} target="_blank" rel="noopener noreferrer" onClick={props.onVisit} data-testid="cta-link">
                  Visit Rising Pixel <span aria-hidden="true">↗</span>
                </a>
              ) : null}
              <button className="next-button" type="button" onClick={props.onNextDemo} disabled={props.loadingNext} aria-label={`Next: ${getDemo(nextDemoId(props.activeDemoId)).title}`} data-testid="next-demo">
                <span>{props.loadingNext ? "Loading" : "Next"}</span><i aria-hidden="true">→</i>
              </button>
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
